/**
 * Copy of AddShipment.jsx, but with some changes as per requirements:
 * - Populated job reference
 */
import { useLazyQuery, useMutation } from "@apollo/client";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { Button, Input, getInput } from "../../../../components/ui";
import { CREATE_SHIPMENT_FROM_JOB as createShipmentMutation } from "../../../../graphql/mutation/Shipment";
import { FETCH_CONTACT as fetchContactQuery } from "../../../../graphql/query/Contact";
import { FETCH_CURRENCIES } from "../../../../graphql/query/Currency";
import useUser from "../../../../hooks/useUser";
import { currentLocaleISODate } from "../../../../util";

const CreateShipment = props => {
  const { closeModal, job, isOpen } = props || {};
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const [createShipment, { loading, error, reset }] = useMutation(
    createShipmentMutation(),
  );
  const [fetchCurrencies, { data: { currencies = [] } = {} }] =
    useLazyQuery(FETCH_CURRENCIES);
  const [fetchContact] = useLazyQuery(fetchContactQuery());
  const [companyNames, setCompanyNames] = useState({
    consignerCompany: "",
    consigneeCompany: "",
  });
  const [warnings, setWarnings] = useState([]);
  const [shipmentItems, setShipmentItems] = useState([]);
  const [shipmentId, setShipmentId] = useState(null);

  const isAllowedToViewCurrency = hasPermission("READ_CURRENCY");

  const formik = useFormik({
    initialValues: {
      date: currentLocaleISODate(),
      jobId: job?.id,
    },
    validationSchema: Yup.object({
      date: Yup.date().required("required"),
      consignorId: Yup.number()
        .positive()
        .required("Please select a consignor"),
      consigneeId: Yup.number()
        .positive()
        .required("Please select a consignee"),
    }),
    onSubmit: async values => {
      const response = await createShipment({
        variables: { input: values },
      });

      if (response?.data?.createShipmentFromJob?.success) {
        formik.resetForm();
        const warnings = response.data.createShipmentFromJob.warnings;
        const shipmentItems = response.data.createShipmentFromJob.shipmentItems;
        const shipmentId = response.data.createShipmentFromJob.newShipmentId;

        setCompanyNames({ consignerCompany: "", consigneeCompany: "" });
        setShipmentItems(() =>
          shipmentItems.filter(item => item.warehouseItemId),
        );
        setWarnings(() => warnings.filter(item => item.instanceId));
        setShipmentId(() => shipmentId);
      } else {
        resetAll();
      }
    },
  });

  const resetAll = () => {
    formik.resetForm();
    reset();
    setShipmentItems([]);
    setWarnings([]);
  };

  useEffect(() => {
    if (isOpen) formik.validateForm();
  }, [isOpen]);

  useEffect(() => {
    if (isAllowedToViewCurrency) {
      fetchCurrencies();
    }
  }, [isAllowedToViewCurrency]);

  // fetch company when consigner or consignee is selected
  useEffect(() => {
    if (formik?.values?.consignorId) {
      fetchContact({ variables: { id: formik?.values?.consignorId } }).then(
        ({ data: { contact } }) => {
          setCompanyNames(prevState => ({
            ...prevState,
            consignerCompany: contact?.company,
          }));
        },
      );
    }
    if (formik?.values?.consigneeId) {
      fetchContact({ variables: { id: formik?.values?.consigneeId } }).then(
        ({ data: { contact } }) => {
          setCompanyNames(prevState => ({
            ...prevState,
            consigneeCompany: contact?.company,
          }));
        },
      );
    }
  }, [formik?.values?.consignorId, formik?.values?.consigneeId]);

  const inputs = [
    { label: "Date - Filled", name: "date", type: "date" },
    {
      label: "Shipper",
      name: "shipperId",
      type: "contactSearch",
      fetchType: "shipper",
    },
    {
      label: "Currency",
      name: "currencyId",
      type: "multi-select",
      options: currencies?.map(currency => ({
        label: `${currency?.description} (${currency?.code})`,
        value: currency?.id,
      })),
    },
    { label: "Shipping Ref", name: "shippingRef", type: "text" },
    { label: "Job Reference", name: "jobId", type: "number", disabled: true },
  ];

  const consignerInput = {
    label: "Consigner",
    name: "consignorId",
    type: "contactSearch",
    showType: true,
  };
  const consigneeInput = {
    label: "Consignee",
    name: "consigneeId",
    type: "contactSearch",
    showType: true,
  };

  const hasErrors = Object.keys(formik?.errors || {}).length > 0;

  if (shipmentItems.length || warnings.length) {
    return (
      <div className="mt-6 flex w-full flex-col">
        <XMarkIcon
          className={`text-black-400 absolute right-3 top-3 h-4 w-4 cursor-pointer`}
          onClick={() => {
            resetAll();
          }}
        />
        {shipmentItems.length > 0 && (
          <div className="ml-auto">
            <Button
              label="Go to Shipment"
              action="primary"
              onClick={() => {
                navigate(`/shipments/${shipmentId}/items`);
              }}
            />
          </div>
        )}
        <div className="flex flex-col">
          <div className="m-2 text-2xl font-bold">
            Added {shipmentItems.length} items
          </div>
          {shipmentItems.length > 0 ? (
            <div className="flex flex-row">
              {shipmentItems.map((item, index) => {
                const { warehouseItemId, instanceId } = item;

                return (
                  <div key={index} className="py-2">
                    Instance :
                    <Link
                      className="inline px-1 underline"
                      to={`/instance/${instanceId}/details`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {instanceId}
                    </Link>
                    WarehouseItem :
                    <Link
                      className="inline px-1 underline"
                      to={`/warehouse/${warehouseItemId}/details`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {warehouseItemId}
                    </Link>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
        <hr className="my-3" />
        {warnings.length ? (
          <div className="flex flex-col">
            <div className="m-2 text-2xl font-bold">Warnings</div>
            <div className="flex flex-col">
              {warnings.map((warning, index) => {
                const { categoryDescription, message, warehouseItemId } =
                  warning;

                return (
                  <div key={index} className="py-2">
                    {categoryDescription}{" "}
                    <Link
                      className="underline"
                      to={`/warehouse/${warehouseItemId}/details`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {warehouseItemId}
                    </Link>
                    : <span>{message}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 flex w-full flex-col">
        <div className="flex flex-row">
          <div className="flex flex-1 flex-col">
            <div className="text-2xl font-bold">Error adding works</div>
          </div>
          <div className="flex flex-col">
            <div>
              <Button
                action="default"
                className="mr-4"
                label="Cancel"
                onClick={() => {
                  resetAll();
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="py-6">
            {error?.message ||
              error?.graphQLErrors?.map?.(({ message }) => `${message} `) ||
              "An error occurred"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col text-2xl font-bold">
          <div>Create Shipment</div>
        </div>
        <div className="flex flex-wrap gap-4">
          <Button action="default" label="Cancel" onClick={closeModal} />
          <Button
            label={"Create"}
            loading={loading}
            disabled={loading || hasErrors}
            onClick={formik.submitForm}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {inputs?.map((item, index) => {
            const inputProps = {
              ...item,
              formik,
            };
            return <div key={index}>{getInput(inputProps)}</div>;
          })}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-4">
            <div>{getInput({ ...consignerInput, formik })}</div>
            <Input
              type="text"
              label="Consignor Company"
              value={companyNames?.consignerCompany}
              disabled
            />
          </div>
          <div className="flex flex-col gap-4">
            <div>{getInput({ ...consigneeInput, formik })}</div>
            <Input
              type="text"
              label="Consignee Company"
              value={companyNames?.consigneeCompany}
              disabled
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateShipment;
